/**
 * Client for the Ops intents (W5, Track A): `POST /api/intents/ops-cancel` — the
 * Ops cancel 7-day split (booking cancelled with its reason, deposit forfeited
 * or kept on file, call outcome stamped, activity line) as ONE server
 * transaction instead of independent write-through PUTs.
 *
 * Same fetch/auth/clock shape and discriminated result as `@/api/intents` (kept
 * in its own module so this track doesn't edit the shared intents client):
 * never throws; a failure carries `kind` ('slot_taken' | 'rejected' |
 * 'transient'). Idempotent on the booking: a replay of a committed cancel is a
 * 200 `alreadyCancelled` no-op, so a retry after a transient failure is safe.
 */
import { authHeaders, isSigningIn, whenSignedIn } from '@/api/persist';
import { intentErrorKind, type IntentFailure } from '@/api/intents';
import { useSimulationStore } from '@/state/simulationStore';
import type { CancelDepositOutcome } from '@atink/domain';

export interface OpsCancelIntentPayload {
  bookingId: string;
  /** Mandatory typed reason (a blank one is refused 400). */
  reason: string;
  by: string;
  /** The confirmation call behind the cancel — stamps its outcome + a 'cancelled' touch. */
  callId?: string;
  /** Override the sim clock (defaults to the facilitator's clock). */
  nowIso?: string;
}

export type OpsCancelIntentResult =
  | { ok: true; alreadyCancelled: boolean; depositOutcome?: CancelDepositOutcome; depositId?: string }
  | IntentFailure;

export async function opsCancelIntent(payload: OpsCancelIntentPayload): Promise<OpsCancelIntentResult> {
  if (isSigningIn()) await whenSignedIn(); // B3: never on a previous persona's token mid-switch
  let res: Response;
  try {
    res = await fetch('/api/intents/ops-cancel', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ ...payload, nowIso: payload.nowIso ?? useSimulationStore.getState().clockIso }),
    });
  } catch {
    return { ok: false, kind: 'transient', status: 0, error: 'network' };
  }
  if (res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      alreadyCancelled?: unknown;
      depositOutcome?: CancelDepositOutcome;
      depositId?: string;
    } | null;
    return {
      ok: true,
      alreadyCancelled: body?.alreadyCancelled === true,
      ...(body?.depositOutcome ? { depositOutcome: body.depositOutcome } : {}),
      ...(body?.depositId ? { depositId: body.depositId } : {}),
    };
  }
  const body = (await res.json().catch(() => undefined)) as
    | { error?: string; code?: string; message?: string }
    | undefined;
  return {
    ok: false,
    kind: intentErrorKind(res.status, body),
    status: res.status,
    error: body?.error ?? `http-${res.status}`,
    ...(typeof body?.code === 'string' ? { code: body.code } : {}),
    ...(body?.message ? { message: body.message } : {}),
  };
}

// ── THE one cancel intent (W5b #1, tv-2na.12.17) ─────────────────────────────

export interface CancelIntentPayload {
  /** The booking to cancel / mark no-show — or `planId` for a plan cancel. */
  bookingId?: string;
  /** A top-up plan: a dated one cancels its booking (and itself); a dateless one just the plan. */
  planId?: string;
  mode: 'cancel' | 'no-show';
  /** Mandatory typed reason (a blank one is refused 400). */
  reason: string;
  /** Optional picklist code logged beside the reason (e.g. 'customer-requested'). */
  reasonCode?: string;
  by: string;
  /** The surface that raised it (customer-record, ops, confirmations, topup-chase, floor...). */
  source?: string;
  callId?: string;
  nowIso?: string;
}

export type CancelIntentResult =
  | {
      ok: true;
      alreadyDone: boolean;
      depositOutcome?: CancelDepositOutcome;
      depositId?: string;
      bookingId?: string;
      topupPlanId?: string;
      amountAud?: number;
    }
  | IntentFailure;

/** POST /api/intents/cancel. Never throws; idempotent (a replay answers alreadyDone). */
export async function cancelIntent(payload: CancelIntentPayload): Promise<CancelIntentResult> {
  if (isSigningIn()) await whenSignedIn(); // B3: never on a previous persona's token mid-switch
  let res: Response;
  try {
    res = await fetch('/api/intents/cancel', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ ...payload, nowIso: payload.nowIso ?? useSimulationStore.getState().clockIso }),
    });
  } catch {
    return { ok: false, kind: 'transient', status: 0, error: 'network' };
  }
  if (res.ok) {
    const body = ((await res.json().catch(() => ({}))) ?? {}) as Record<string, unknown>;
    const s = (k: string) => (typeof body[k] === 'string' ? (body[k] as string) : undefined);
    return {
      ok: true,
      alreadyDone: body.alreadyDone === true,
      ...(s('depositOutcome') ? { depositOutcome: s('depositOutcome') as CancelDepositOutcome } : {}),
      ...(s('depositId') ? { depositId: s('depositId') } : {}),
      ...(s('bookingId') ? { bookingId: s('bookingId') } : {}),
      ...(s('topupPlanId') ? { topupPlanId: s('topupPlanId') } : {}),
      ...(typeof body.amountAud === 'number' ? { amountAud: body.amountAud } : {}),
    };
  }
  const body = (await res.json().catch(() => undefined)) as { error?: string; code?: string; message?: string } | undefined;
  return {
    ok: false,
    kind: intentErrorKind(res.status, body),
    status: res.status,
    error: body?.error ?? `http-${res.status}`,
    ...(typeof body?.code === 'string' ? { code: body.code } : {}),
    ...(body?.message ? { message: body.message } : {}),
  };
}
